import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import bgImage from "../../Images/login.svg";
import Logo from "../../Images/Logo.svg";
import Lock from "../../Images/lock.svg";
import { API_URL } from "../../config/api";
import ToastMessage from "../../config/ToastMessage";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(null);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    type: "",
    message: "",
  });

  // Validate token immediately
  useEffect(() => {
    if (token) {
      axios
        .post(`${API_URL}/validate-reset-token`, null, {
          params: { token },
        })
        .then(() => setValidToken(true))
        .catch((err) => {
          setValidToken(false);
          setError(err.response?.data?.detail || "Invalid or expired token");
        });
    } else {
      setValidToken(false);
      setError("No token provided");
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setToast({ type: "", message: "" });

    // Frontend validation
    if (!formData.newPassword) return setError("New password is required");
    if (!formData.confirmPassword)
      return setError("Confirm password is required");
    if (formData.newPassword !== formData.confirmPassword)
      return setError("Passwords do not match");
    if (formData.newPassword.length < 8)
      return setError("Password must be at least 8 characters long");

    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/reset-password`, {
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });

      // Check backend success response
      if (res.data.success) {
        setToast({
          type: "success",
          message: res.data.message || "Password reset successfully!",
        });

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        // In case backend returns success=false
        setToast({
          type: "error",
          message: res.data.message || "Failed to reset password",
        });
      }
    } catch (err) {
      // Robust error handling for production
      let msg = "Something went wrong";

      if (err.response?.data) {
        const data = err.response.data;

        if (Array.isArray(data.detail) && data.detail.length > 0) {
          // Pydantic validation errors
          msg = data.detail[0].msg;
        } else if (typeof data.detail === "string") {
          msg = data.detail;
        } else if (data.message) {
          msg = data.message;
        }
      }

      setToast({
        type: "error",
        message: msg,
      });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-end bg-gray-50"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Toast */}
      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 bg-white rounded-2xl shadow-[0_12px_24px_rgba(32,33,35,0.1)] border border-zinc-200 w-[90%] max-w-md p-8 flex flex-col gap-6 mr-20">
        <div className="flex justify-start mb-1">
          <img src={Logo} alt="SkillsMD Logo" className="w-36 h-auto" />
        </div>

        <div className="w-16 h-16 bg-white rounded-full border-8 border-gray-200 flex items-center justify-center">
          <div className="w-8 h-8 bg-gradient-to-br  rounded-full flex items-center justify-center">
            <img src={Lock} alt="lockicon" />
          </div>
        </div>

        <div className="text-left">
          <h1 className="text-zinc-800 text-lg font-bold font-['Roboto'] mb-2">
            Reset Your Password
          </h1>
          <p className="text-zinc-600 text-sm font-normal font-['Roboto'] leading-5">
            Reset your password below to continue using your account
          </p>
        </div>

        {/* Show loading state */}
        {validToken === null && (
          <div className="flex justify-center items-center py-8">
            <p className="text-gray-600">Validating token...</p>
          </div>
        )}

        {/* Show form only if token is valid */}
        {validToken === true && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-start gap-2 relative">
              <label className="text-sm font-bold text-neutral-800">
                New Password <span className="text-red-600">*</span>
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter Password"
                className="h-10 w-full px-3 pr-10 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div className="flex flex-col items-start gap-2 relative">
              <label className="text-sm font-bold text-neutral-800">
                Confirm Password <span className="text-red-600">*</span>
              </label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Enter Password"
                className="h-10 w-full px-3 pr-10 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <p className="text-red-600 text-xs font-normal font-['Roboto']">
                {error}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`mt-1 w-full px-5 py-2.5 rounded-md text-white text-sm font-bold shadow-sm transition-all ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#4AACC5] hover:bg-[#3E95AC]"
              }`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        )}

        {/* Show error message if token is invalid */}
        {validToken === false && (
          <div className="flex flex-col gap-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-600 text-sm font-medium mb-2">
                {error}
              </p>
              <p className="text-red-500 text-xs">
                The password reset link is invalid or has expired. Please request a new one.
              </p>
            </div>
            <button
              onClick={() => navigate("/passwordlink")}
              className="w-full px-5 py-2.5 rounded-md bg-[#4AACC5] hover:bg-[#3E95AC] text-white text-sm font-bold transition-all"
            >
              Back
            </button>
          </div>
        )}

        <div className="self-stretch text-left">
          <p className="text-zinc-800 text-xs font-normal leading-5">
            By continuing, you agree to{" "}
            <span className="text-[#4AACC5] font-medium cursor-pointer">
              SkillsMD
            </span>{" "}
            Agreement or other agreement for, and the Privacy Notice. This site
            uses essential cookies. See our Cookie Notice for more information.
          </p>
        </div>
      </div>
    </div>
  );
}